import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { message } from 'antd'

import requests from '../../axios/requests'
import { setUser } from '../../redux/actions/userActions'
import { setConfig, setLoadingGpt, setLoadingIndx, setSelectedTag } from '../../redux/actions/actions'
import variableLanguage from '../../../utils/hookGetVariablesLanguage'
import UnAuthenticated from './UnAuthenticated/UnAuthenticated'

import useTrajectory from '../SkillSet/gen/useTrajectory'
import { PlanCard, PlanEmpty, PlanGenerating, PlanModal } from '../SkillSet/gen/PlanCard'
import { pluralN, skillStatus } from '../SkillSet/gen/genUtils'
import s from '../SkillSet/gen/gen.module.scss'
import p from '../SkillSet/gen/plans.module.scss'

const LANGS = ['RU', 'EN', 'SA']

const GenerateCourseSkillSet = () => {
  const variable_15 = variableLanguage({ keyPage: 'generateCourse', keyVariable: 'variable_15' })

  const history = useNavigate()
  const dispatch = useDispatch()
  const { user, config, selectedTag, loadingIndx } = useSelector((state) => state.Reducer)
  const trajectory = useTrajectory()
  const authed = !!localStorage.getItem('token')

  const slots = Number(config?.PLAN_GENERATION_UPDATE_LIMIT) || 4
  const [plans, setPlans] = useState([])
  const [lang, setLang] = useState('RU')
  const [creatingId, setCreatingId] = useState(null)
  const [modalPlan, setModalPlan] = useState(null)
  const [favPage, setFavPage] = useState(false)
  const [favorites, setFavorites] = useState({ results: [], count: 0 })
  const [favTag, setFavTag] = useState(null)
  const [favCurrent, setFavCurrent] = useState(1)
  const pollRef = useRef(null)

  const limit = user?.education_plan_limit || 0
  const generating = loadingIndx !== null && loadingIndx !== undefined

  const fetchPlans = (tag = selectedTag) => {
    if (!tag?.id) return Promise.resolve()
    return requests.users
      .fetch_education_plan(tag.id)
      .then((res) => {
        const data = res.data?.results || []
        setPlans(data)
        return data
      })
      .catch((err) => console.error(err))
  }

  const fetchProfile = () =>
    requests.users
      .get_profile_new()
      .then((res) => dispatch(setUser(res.data)))
      .catch((err) => console.error(err))

  // прогресс celery-задачи генерации плана
  const waitTask = (taskId) => {
    requests.users
      .calery_progress(taskId)
      .then((res) => {
        if (res.data?.progress?.current !== 100) {
          pollRef.current = setTimeout(() => waitTask(taskId), 2000)
          return
        }
        fetchPlans().then(() => {
          dispatch(setLoadingGpt(false))
          dispatch(setLoadingIndx(null))
          trajectory.refresh()
        })
      })
      .catch((err) => {
        console.error(err)
        dispatch(setLoadingGpt(false))
        dispatch(setLoadingIndx(null))
      })
  }

  const generatePlan = () => {
    if (!selectedTag?.id || generating) return
    if (plans.length === 0 && limit === 0) return message.error(variable_15)
    dispatch(setLoadingIndx(plans.length))
    dispatch(setLoadingGpt(true))
    requests.users
      .education_plan({ speciality_tag: selectedTag.id, language: lang.toLowerCase() })
      .then((res) => waitTask(res.data.task_id))
      .catch((err) => {
        dispatch(setLoadingGpt(false))
        dispatch(setLoadingIndx(null))
        const data = err?.response?.data
        if (Array.isArray(data)) data.forEach((m) => message.error(m))
        else message.error('Не удалось запустить генерацию плана')
      })
  }

  const createCourse = (plan) => {
    if (limit === 0) return message.error(variable_15)
    setCreatingId(plan.id)
    requests.users
      .generate_quest(plan.id)
      .then((res) => {
        dispatch(setUser({ ...user, education_plan_limit: Math.max(0, limit - 1) }))
        trajectory.refresh()
        const questId = res.data?.quest?.id
        if (questId) history(`/student/course/lesson/${questId}`)
        else fetchPlans()
      })
      .catch((err) => {
        console.error(err)
        message.error(err?.response?.data?.detail || 'Не удалось создать курс')
      })
      .finally(() => setCreatingId(null))
  }

  const toggleFavorite = (plan) =>
    requests.users
      .changeFavoritePlan(plan.id, !plan.favorite)
      .then(() => {
        setPlans((prev) => prev.map((x) => (x.id === plan.id ? { ...x, favorite: !plan.favorite } : x)))
        if (favPage) fetchFavorites()
        trajectory.refresh()
      })
      .catch((err) => console.error(err))

  const fetchFavorites = () =>
    requests.users
      .fetchFavorites(favCurrent, slots, favTag)
      .then(({ data }) => setFavorites(data || { results: [], count: 0 }))
      .catch((err) => console.error(err))

  const openCourse = (plan) => history(`/student/course/lesson/${plan.quest.id}`)

  useEffect(() => {
    fetchProfile()
    trajectory.refresh()
    requests.users
      .get_config()
      .then((res) => dispatch(setConfig(res.data)))
      .catch((err) => console.error(err))
    return () => clearTimeout(pollRef.current)
  }, [])

  // навык по умолчанию: первый без курса
  useEffect(() => {
    if (selectedTag?.id || !trajectory.list.length) return
    const first = trajectory.list.find((x) => skillStatus(x).kind !== 'course') || trajectory.list[0]
    if (first && typeof first.id === 'number') dispatch(setSelectedTag(first))
  }, [trajectory.list, selectedTag])

  useEffect(() => {
    setPlans([])
    if (selectedTag?.id) fetchPlans(selectedTag)
  }, [selectedTag])

  // если план генерировался, пока страница была закрыта, — подтягиваем его, когда он будет готов
  useEffect(() => {
    if (generating || !plans.some((x) => x.generation_in_progress)) return
    const t = setTimeout(() => fetchPlans(), 4000)
    return () => clearTimeout(t)
  }, [plans, generating])

  useEffect(() => {
    if (favPage) fetchFavorites()
  }, [favPage, favCurrent, favTag])

  if (!authed) return <UnAuthenticated sizeTrajectory={false} setSizeTrajectory={() => {}} />

  const stageHead = (
    <div className={s.stagehead}>
      <div>
        <div className={s.kicker}>Шаг 2 из 3</div>
        <h2>{favPage ? 'Избранные планы' : 'Генерация курса'}</h2>
        <div className={cn(s.muted, s.small)}>
          {favPage
            ? 'Планы, которые вы отметили звёздочкой, по всем навыкам'
            : 'ИИ предложит до четырёх вариантов плана по выбранному навыку'}
        </div>
      </div>
    </div>
  )

  if (favPage) {
    const favSkills = trajectory.list.filter((x) => x.favorite)
    const pages = Math.max(1, Math.ceil((favorites.count || 0) / slots))
    return (
      <section className={s.stage} aria-label="Избранные планы">
        {stageHead}
        <div className={p.head}>
          <button type="button" className={cn(s.btn, s.ghost, s.sm)} onClick={() => setFavPage(false)}>
            ← К вариантам
          </button>
          {favSkills.length > 0 && (
            <div className={p.favSkills} role="group" aria-label="Фильтр по навыку">
              {favSkills.map((sk) => (
                <button
                  type="button"
                  key={sk.id}
                  className={cn(s.pchip, s.pchipBtn, { [s.pchipOn]: favTag === sk.id })}
                  aria-pressed={favTag === sk.id}
                  onClick={() => {
                    setFavCurrent(1)
                    setFavTag(favTag === sk.id ? null : sk.id)
                  }}
                >
                  {sk.name}
                </button>
              ))}
            </div>
          )}
        </div>
        {favorites.results?.length ? (
          <div className={p.grid}>
            {favorites.results.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                title={`Избранный план ${(favCurrent - 1) * slots + i + 1}`}
                busy={creatingId !== null}
                onCreate={() => createCourse(plan)}
                onOpenCourse={() => openCourse(plan)}
                onFavorite={() => toggleFavorite(plan)}
                onExpand={() => setModalPlan({ plan, title: 'Избранный план' })}
                createNote="Спишется 1 генерация"
              />
            ))}
          </div>
        ) : (
          <div className={cn(s.card, s.empty)}>Пока нет избранных планов — отметьте понравившийся вариант звёздочкой</div>
        )}
        {pages > 1 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.sm)}
              disabled={favCurrent <= 1}
              onClick={() => setFavCurrent(favCurrent - 1)}
              aria-label="Предыдущая страница"
            >
              ‹
            </button>
            <span className={cn(s.muted, s.small)}>
              {favCurrent} из {pages}
            </span>
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.sm)}
              disabled={favCurrent >= pages}
              onClick={() => setFavCurrent(favCurrent + 1)}
              aria-label="Следующая страница"
            >
              ›
            </button>
          </div>
        )}
        <PlanModal
          open={!!modalPlan}
          plan={modalPlan?.plan}
          title={modalPlan?.title}
          onClose={() => setModalPlan(null)}
          footer={
            modalPlan?.plan && !modalPlan.plan.quest?.id ? (
              <button
                type="button"
                className={cn(s.btn, s.pri)}
                disabled={creatingId !== null}
                onClick={() => createCourse(modalPlan.plan)}
              >
                Создать курс по этому плану
              </button>
            ) : null
          }
        />
      </section>
    )
  }

  const readyCount = plans.filter((x) => !x.generation_in_progress).length

  return (
    <section className={s.stage} aria-label="Генерация курса">
      {stageHead}

      {!trajectory.list.length ? (
        <div className={cn(s.card, s.empty)}>
          <h3 style={{ marginBottom: 6 }}>Учебный план пока пуст</h3>
          <div style={{ marginBottom: 14 }}>Добавьте навыки на шаге «Обзор профессий» или через поле в учебном плане</div>
          <button type="button" className={cn(s.btn, s.pri)} onClick={() => history('/backoffice/skill-set/prof-review')}>
            К обзору профессий
          </button>
        </div>
      ) : !selectedTag?.id ? (
        <div className={cn(s.card, s.empty)}>Выберите навык в учебном плане выше — покажем варианты плана по нему</div>
      ) : (
        <>
          <div className={cn(s.card, p.head)}>
            <div>
              <div className={cn(s.muted, s.small)}>
                Планы обучения по навыку · {readyCount} из {pluralN(slots, ['варианта', 'вариантов', 'вариантов'])}
              </div>
              <h2>{selectedTag.name}</h2>
            </div>
            <div className={p.headRight}>
              <span className={cn(s.muted, s.small)}>Язык нового плана</span>
              <div className={s.seg} role="group" aria-label="Язык плана">
                {LANGS.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={cn({ [s.segOn]: lang === l })}
                    aria-pressed={lang === l}
                    onClick={() => setLang(l)}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <button type="button" className={cn(s.btn, s.ghost, s.sm)} onClick={() => setFavPage(true)}>
                ★ Избранные планы
              </button>
            </div>
          </div>

          <div className={p.grid}>
            {Array.from({ length: slots }).map((_, i) => {
              const plan = plans[i]
              const title = `Вариант ${i + 1}`
              if (plan && plan.generation_in_progress) return <PlanGenerating key={plan.id} title={title} />
              if (plan)
                return (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    title={title}
                    busy={creatingId !== null || generating}
                    onCreate={() => createCourse(plan)}
                    onOpenCourse={() => openCourse(plan)}
                    onFavorite={() => toggleFavorite(plan)}
                    onExpand={() => setModalPlan({ plan, title })}
                    createNote={creatingId === plan.id ? 'Создаём курс…' : 'Спишется 1 генерация'}
                  />
                )
              if (generating && i === loadingIndx) return <PlanGenerating key={`g${i}`} title={title} lang={lang} />
              return (
                <PlanEmpty
                  key={`e${i}`}
                  title={title}
                  disabled={generating || i !== plans.length}
                  onGenerate={generatePlan}
                  note={
                    i !== plans.length
                      ? 'Станет доступен после предыдущего варианта'
                      : plans.length === 0 && limit === 0
                      ? 'Нужна хотя бы 1 генерация на балансе'
                      : 'План бесплатный — генерация спишется при создании курса'
                  }
                />
              )
            })}
          </div>
        </>
      )}

      <PlanModal
        open={!!modalPlan}
        plan={modalPlan?.plan}
        title={modalPlan?.title}
        onClose={() => setModalPlan(null)}
        footer={
          modalPlan?.plan && !modalPlan.plan.quest?.id ? (
            <button
              type="button"
              className={cn(s.btn, s.pri)}
              disabled={creatingId !== null}
              onClick={() => createCourse(modalPlan.plan)}
            >
              Создать курс по этому плану
            </button>
          ) : null
        }
      />
    </section>
  )
}

export default GenerateCourseSkillSet
